import React from 'react';

/**
 * productIcons — one source of truth for the per-product line icon + accent
 * tint used across the site.
 *
 * The paths and tints are lifted verbatim from the Chrome extension's side
 * panel (`chrome-extension/sidepanel.js` — the `ic()` helper, `TOOLS`, and
 * `TOOL_TINTS`) so a user sees the same mark and the same colour for a tool
 * whether they're in the extension or on the website.
 *
 * Keyed by route path, which is stable — display names differ between
 * surfaces ("Interview Copilot" vs "Interview Assistant", "LinkedIn" vs
 * "LinkedIn Optimizer") but the destination doesn't.
 *
 * Course Creator has no extension counterpart; its mark and rose tint were
 * drawn to match the set. If you add it to the extension, copy from here.
 */
const PRODUCTS = {
  '/AutoJobApply': {
    tint: '#a78bfa',
    path: <path d="M13 2 3 14h9l-1 8 10-12h-9z" />,
  },
  '/mockInterview': {
    tint: '#22d3ee',
    path: <><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10a7 7 0 0 0 14 0M12 19v3" /></>,
  },
  '/InterviewAssistant': {
    tint: '#f472b6',
    path: <><rect x="4" y="8" width="16" height="12" rx="2" /><path d="M12 8V5M9 13h.01M15 13h.01M9 17h6" /><path d="M2 13v2M22 13v2" /></>,
  },
  '/resume-builder': {
    tint: '#4ade80',
    path: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M8 13h8M8 17h6" /></>,
  },
  '/ATSCheck': {
    tint: '#38bdf8',
    path: <><circle cx="12" cy="12" r="9" /><path d="m8 12 3 3 5-6" /></>,
  },
  '/CoverLetter': {
    tint: '#fbbf24',
    path: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
  },
  '/InterviewQuestions': {
    tint: '#c084fc',
    path: <><circle cx="12" cy="12" r="9" /><path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.8.4-1 .8-1 1.7M12 17h.01" /></>,
  },
  '/LinkedInOptimizer': {
    tint: '#2dd4bf',
    path: <><path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" /><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" /></>,
  },
  '/portfolio': {
    tint: '#818cf8',
    path: <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></>,
  },
  '/certification': {
    tint: '#fb7185',
    path: <><path d="M12 3 2 8l10 5 10-5-10-5z" /><path d="M6 10.5V16c0 1.7 2.7 3 6 3s6-1.3 6-3v-5.5M20 9v5" /></>,
  },
};

/** Accent colour for a product, or a neutral fallback for unknown paths. */
export const productTint = (path) => PRODUCTS[path]?.tint || '#a78bfa';

/**
 * The product's line mark. Inherits `currentColor`, so the caller sets the
 * colour — same contract as the extension's `ic()` helper.
 */
export const ProductIcon = ({ path, size = 17, className = '' }) => {
  const entry = PRODUCTS[path];
  if (!entry) return null;
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {entry.path}
    </svg>
  );
};

export default ProductIcon;
